import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const CAPACITIES = [4, 10, 36, 38, 72, 120, 409];
const COLORS = ["#6366F1", "#8B5CF6", "#06B6D4", "#10B981", "#F59E0B", "#EF4444", "#EC4899"];

const slugify = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50);

export default function RoomWizard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null);
  const [parents, setParents] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", parent_id: "", access_type: "public", description: "", capacity: 10, color: "#6366F1" });

  useEffect(() => {
    api.get("/rooms").then(({ data }) => setParents(data.owned.filter((r) => !r.parent_id))).catch(() => {});
  }, []);

  const parentRoom = parents.find((p) => p.id === form.parent_id);
  const fullSlug = parentRoom ? `${parentRoom.full_slug}/${form.slug || "nazev"}` : (form.slug || "nazev");

  const setName = (name) => setForm({ ...form, name, slug: slugify(name) });

  const checkSlug = async () => {
    if (!form.slug) return;
    try {
      const { data } = await api.post("/rooms/check-slug", { slug: form.slug, parent_id: form.parent_id });
      setSlugStatus(data);
    } catch {}
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/rooms", { ...form, plan: "monthly" });
      toast.success(t("wizard.created"));
      navigate(`/rooms/${data.id}/manage`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const steps = [t("wizard.step_basic"), t("wizard.step_access"), t("wizard.step_branding")];
  const canNext = step === 0 ? form.name.length >= 3 && form.slug.length >= 3 : true;

  return (
    <div className="min-h-screen bg-[#0B0F17] mesh-bg">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 pt-28 pb-20" data-testid="room-wizard">
        <Link to="/dashboard" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t("wizard.back_overview")}
        </Link>
        <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white">{t("wizard.title")}</h1>

        <div className="mt-8 flex items-center gap-2">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-indigo-500" : "bg-slate-800"}`} />
              <span className={`mt-2 block text-xs ${i === step ? "text-indigo-400 font-semibold" : "text-slate-600"}`}>{s}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-[#131B2A] border border-slate-800 rounded-2xl p-8">
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("wizard.name_label")}</label>
                <input data-testid="wizard-name-input" value={form.name} onChange={(e) => setName(e.target.value)}
                  placeholder={t("wizard.name_ph")}
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("wizard.parent_label")}</label>
                <select data-testid="wizard-parent-select" value={form.parent_id}
                  onChange={(e) => { setForm({ ...form, parent_id: e.target.value }); setSlugStatus(null); }}
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white focus:outline-none focus:border-indigo-500">
                  <option value="">{t("wizard.parent_none")}</option>
                  {parents.map((p) => <option key={p.id} value={p.id}>{p.name} (digisidlo.cz/{p.full_slug})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("wizard.slug_label")}</label>
                <div className="mt-2 flex items-center gap-2">
                  <input data-testid="wizard-slug-input" value={form.slug}
                    onChange={(e) => { setForm({ ...form, slug: slugify(e.target.value) }); setSlugStatus(null); }}
                    onBlur={checkSlug}
                    placeholder="akstancik"
                    className="flex-1 px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 font-mono text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500" />
                </div>
                <p className="mt-2 font-mono text-xs text-indigo-300" data-testid="wizard-address-preview">
                  digisidlo.cz/{fullSlug}
                </p>
                {slugStatus && (
                  <p data-testid="slug-status" className={`mt-1 text-xs ${slugStatus.available ? "text-emerald-400" : "text-red-400"}`}>
                    {slugStatus.available ? t("wizard.slug_free") : slugStatus.valid ? t("wizard.slug_taken") : t("wizard.slug_invalid")}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("wizard.desc_label")}</label>
                <textarea data-testid="wizard-description-input" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                  placeholder={t("wizard.desc_ph")}
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-[#0B0F17] border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500 resize-none" />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-8">
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("wizard.capacity_label")}</label>
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {CAPACITIES.map((c) => (
                    <button key={c} data-testid={`wizard-capacity-${c}`} onClick={() => setForm({ ...form, capacity: c })}
                      className={`py-3 rounded-xl font-mono text-sm font-semibold border transition-colors ${form.capacity === c ? "bg-indigo-600 border-indigo-500 text-white" : "bg-[#0B0F17] border-slate-700 text-slate-400 hover:border-indigo-500"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("wizard.access_label")}</label>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["public", "private"].map((a) => (
                    <button key={a} data-testid={`wizard-access-${a}`} onClick={() => setForm({ ...form, access_type: a })}
                      className={`p-5 rounded-xl border text-left transition-colors ${form.access_type === a ? "bg-indigo-950/50 border-indigo-500" : "bg-[#0B0F17] border-slate-700 hover:border-indigo-500"}`}>
                      <div className="font-semibold text-white text-sm">{t(a === "public" ? "wizard.public_title" : "wizard.private_title")}</div>
                      <div className="mt-1.5 text-xs text-slate-500 leading-relaxed">{t(a === "public" ? "wizard.public_desc" : "wizard.private_desc")}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-semibold text-slate-300">{t("wizard.color_label")}</label>
                <div className="mt-3 flex flex-wrap gap-3">
                  {COLORS.map((c) => (
                    <button key={c} data-testid={`wizard-color-${c.slice(1)}`} onClick={() => setForm({ ...form, color: c })}
                      className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${form.color === c ? "border-white scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-slate-800 p-5 bg-[#0B0F17]">
                <span className="text-xs uppercase tracking-widest text-slate-500">{t("wizard.preview")}</span>
                <div className="mt-3 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl" style={{ backgroundColor: form.color }} />
                  <div>
                    <div className="font-semibold text-white">{form.name || t("wizard.name_label")}</div>
                    <div className="font-mono text-xs text-indigo-300">digisidlo.cz/{fullSlug} · {form.capacity} {t("room.seats")} · {t(form.access_type === "public" ? "room.public" : "room.private")}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button data-testid="wizard-back-button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-700 text-slate-300 text-sm font-semibold hover:border-indigo-500 transition-colors disabled:opacity-40">
              <ArrowLeft className="w-4 h-4" /> {t("wizard.back")}
            </button>
            {step < 2 ? (
              <button data-testid="wizard-next-button" onClick={() => setStep(step + 1)} disabled={!canNext}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 transition-colors disabled:opacity-40">
                {t("wizard.next")} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button data-testid="wizard-submit-button" onClick={submit} disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-50">
                <Check className="w-4 h-4" /> {loading ? t("wizard.creating") : t("wizard.create")}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
